import { Button, Checkbox, Input } from '@heroui/react'
import { usePermanentDeleteTenant } from '@qse/api'
import type { ModalComponentProps } from '@qse/constants'
import { QSE_ENTERPRISE_ID } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

interface DeleteTenantConfirmationData {
  data: {
    tenant: {
      id: string
      name: string
      resellerId: string
      status: string
    }
    onSuccess?: () => void
  }
}

export default function DeleteTenantConfirmationModal(props: ModalComponentProps) {
  const { onClose } = props
  const { data } = useActiveModelStore()
  const [confirmText, setConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [shouldSkipRetention, setShouldSkipRetention] = useState(false)
  const isDeletingRef = useRef(false)

  const modalData = data as DeleteTenantConfirmationData
  const { mutateAsync: deleteTenant } = usePermanentDeleteTenant()

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      return
    }

    // Prevent double-submit: check ref synchronously before any async operations
    if (isDeletingRef.current) {
      return
    }

    try {
      // Set both ref (synchronous) and state (for UI)
      isDeletingRef.current = true
      setIsDeleting(true)
      await deleteTenant({
        id: modalData.data.tenant.id,
        skipRetention: shouldSkipRetention || undefined,
      })

      // Clear confirmation text immediately to disable button
      // This prevents any clicks during the brief moment before modal closes
      setConfirmText('')
      setShouldSkipRetention(false)

      if (modalData.data.onSuccess) {
        modalData.data.onSuccess()
      }

      // Close modal using the proper method
      // Note: We don't reset deleting states on success to keep button disabled until modal closes
      onClose()
    } catch (error: any) {
      console.error('Failed to delete tenant:', error)

      // Only reset states on error to allow retry
      isDeletingRef.current = false
      setIsDeleting(false)

      toast.error('Failed to delete tenant', {
        description:
          error?.response?.data?.message ||
          'An error occurred while deleting the tenant. Please try again.',
      })
    }
  }

  const handleCancel = () => {
    setConfirmText('')
    setShouldSkipRetention(false)
    onClose()
  }

  const isDeleteEnabled = confirmText === 'DELETE'
  const isQSEEnterprise = modalData?.data?.tenant?.resellerId === QSE_ENTERPRISE_ID
  const tenantType = isQSEEnterprise ? 'QSE Enterprise' : 'QSE Retail'

  return (
    <div className="p-3 lg:p-6">
      <div className="flex flex-col items-center mb-6">
        <p className="font-medium">
          This will permanently delete the tenant and all associated data after 7 days. This action
          cannot be undone.
        </p>
      </div>

      {/* Tenant Details */}
      <div className="bg-gray-50 dark:bg-[#1E1E1E] border border-gray-200 dark:border-gray-700/50 rounded-lg py-4 px-5 mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-base">
          Tenant Details:
        </h3>
        <div className="space-y-2.5">
          <div className="flex items-start gap-2 min-w-0">
            <span className="text-gray-600 dark:text-gray-400 text-sm font-medium flex-shrink-0">
              Company:
            </span>
            <span
              className="font-semibold text-gray-900 dark:text-gray-100 truncate text-sm flex-1 min-w-0"
              title={modalData?.data?.tenant?.name}
            >
              {modalData?.data?.tenant?.name}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Type:</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
              {tenantType}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-3">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Check the box below to bypass the grace period and schedule deletion within the next 24
          hours.
        </p>
        <Checkbox
          isDisabled={isDeleting}
          isSelected={shouldSkipRetention}
          classNames={{
            label: 'text-sm font-semibold text-gray-900 dark:text-gray-100',
          }}
          onValueChange={setShouldSkipRetention}
        >
          Delete in next cleanup window (within 24 hours)
        </Checkbox>
      </div>

      {/* Confirmation Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
          To proceed, type <span className="font-bold">DELETE</span> below:
        </label>
        <Input
          isDisabled={isDeleting}
          placeholder="Type DELETE to confirm"
          value={confirmText}
          variant="bordered"
          classNames={{
            input: [
              'bg-transparent',
              'text-gray-900 dark:text-white',
              'placeholder:text-gray-500 dark:placeholder:text-gray-400',
            ],
            inputWrapper: [
              'border-gray-300 dark:border-gray-600',
              'hover:border-gray-400 dark:hover:border-gray-500',
            ],
          }}
          onChange={e => setConfirmText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && confirmText === 'DELETE') {
              e.preventDefault()
              handleDelete()
            }
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <Button
          color="default"
          isDisabled={isDeleting}
          radius="sm"
          variant="flat"
          onPress={handleCancel}
        >
          Cancel
        </Button>
        <Button
          color="danger"
          isDisabled={!isDeleteEnabled || isDeleting}
          isLoading={isDeleting}
          radius="sm"
          variant="solid"
          onPress={handleDelete}
        >
          Delete Tenant
        </Button>
      </div>
    </div>
  )
}
